@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.entrys

import org.example.project.manager.NodeScope

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardNode
import org.example.project.dashboard.DashboardScreen
import org.example.project.dashboard.DriverDashboardScreen
import org.example.project.dashboard.ScreenManager
import org.example.project.enginechat.MobileChatLayout
import org.example.project.login.PasswordResetScreen
import org.example.project.manager.LumaticaApiSettings
import org.example.project.registrierung.ZweiFaktorRecoveryBereich
import org.example.project.registrierung.ZweiFaktorRecoveryScreen
import org.example.project.registrierung.DesktopRegistrierungScreen
import org.example.project.registrierung.MobileFahrerRegistrierungScreen
import org.example.project.registrierung.CompanionLoginScreen
import org.example.project.screens_driver.DRIVERMeineAuftraege
import org.example.project.screens_driver.DRIVERMeineAuftragsmappe
import org.example.project.screens_driver.DRIVERYardLobbyScreen
import org.example.project.screens_driver.DRIVERProfilAuswahl
import org.example.project.screens_driver.Fahrerkalender
import org.example.project.screens_shared.MobileNotepadLayout
import org.example.project.service.LoginService
import org.example.project.service.MobileAnsicht
import org.example.project.service.MobileAnsichtenApi
import org.example.project.service.RollenService
import org.example.project.service.ScreenConfigApi
import org.example.project.service.SystemPushService
import org.example.project.service.shouldForceLogoutAfterBuildChange
import org.example.project.utils.All
import org.w3c.dom.events.Event

private fun currentNetPath(): String {
    val path = window.location.pathname.trimEnd('/').lowercase().ifBlank { "/" }
    return when (path) {
        "/register" -> "/login"
        "/", "/login", "/driver", "/companion", "/mobile", "/passwort-reset", "/zwei-faktor-reset" -> path
        else -> ""
    }
}

private fun driverMobileScreens(): List<All> = listOf(
    All(
        key = "MobileNotepadSystem",
        label = "Notizen",
        icon = "images/note.svg",
        content = { MobileNotepadLayout(NodeScope.NET) }
    ),
    All(
        key = "Fahrerkalender",
        label = "Fahrerkalender",
        icon = "images/kalender.svg",
        content = { Fahrerkalender() }
    ),
    All(
        key = "DriverMeineAuftraege",
        label = "Meine Aufträge",
        icon = "images/order.svg",
        content = { DRIVERMeineAuftraege() }
    ),
    All(
        key = "DriverMeineAuftragsmappe",
        label = "Meine Auftragsmappe",
        icon = "images/mappe.svg",
        content = { DRIVERMeineAuftragsmappe() }
    ),
    All(
        key = "DriverProfileScreen",
        label = "Mein Profil",
        icon = "images/user.svg",
        content = { DRIVERProfilAuswahl() }
    ),
    All(
        key = "ChatModuleScreen",
        label = "Chat",
        icon = "images/chat.svg",
        content = { MobileChatLayout(art = NodeScope.NET) }
    ),
    All(
        key = "DriverYardLobbyScreen",
        label = "Hofauftrag",
        icon = "images/lager.svg",
        hiddenFromLauncher = true,
        content = { DRIVERYardLobbyScreen() }
    )
)


@Composable
fun NETEntry() {
    LumaticaApiSettings.nodeIp = window.location.hostname
    LumaticaApiSettings.nodePort = window.location.port

    var path by remember { mutableStateOf(currentNetPath()) }
    var screen by remember {
        mutableStateOf(
            when (currentNetPath()) {
                "/driver" -> "DRIVER_LOGIN"
                "/mobile" -> "DRIVER_LOGIN"
                "/companion" -> "COMPANION_LOGIN"
                "/", "/login" -> "NETZWERK_LOGIN"
                else -> "NONE"
            }
        )
    }
    var sessionReady by remember { mutableStateOf(false) }
    var mobileAnsichten by remember { mutableStateOf<List<MobileAnsicht>>(emptyList()) }
    val screenConfigVersion = ScreenConfigApi.version()
    val scope = rememberCoroutineScope()
    if (!LoginService.isLoggedIn()) {
        org.example.project.login.PublicWebsiteFooter(
            compact = path in setOf("/mobile", "/companion", "/driver", "/driver/register")
        )
    }

    LaunchedEffect(Unit) {
        val legacyPath = window.location.pathname.trimEnd('/').lowercase()
        if (legacyPath == "/register" || legacyPath == "/driver/register") {
            window.history.replaceState(null, "", if (legacyPath == "/driver/register") "/driver" else "/login")
        }
    }

    suspend fun initializeLoggedInSession(): Boolean {
        SystemPushService.ensureStarted()
        ScreenManager.applyScreenConfig(ScreenConfigApi.loadScreenConfig())
        RollenService.refreshCurrentUserRole(NodeScope.NET)
        mobileAnsichten = MobileAnsichtenApi.all()
        return true
    }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { path = currentNetPath() }
        window.addEventListener("hashchange", listener)
        window.addEventListener("popstate", listener)
        onDispose {
            window.removeEventListener("hashchange", listener)
            window.removeEventListener("popstate", listener)
        }
    }

    LaunchedEffect(path) {
        screen = when (path) {
            "/driver" -> if (LoginService.isLoggedIn() && LoginService.getUser()?.zugangMobil == true) "DRIVER_DASHBOARD" else "DRIVER_LOGIN"
            "/mobile" -> if (LoginService.isLoggedIn() && LoginService.getUser()?.zugangMobil == true) "DRIVER_DASHBOARD" else "DRIVER_LOGIN"
            "/companion" -> if (LoginService.isLoggedIn() && LoginService.getUser()?.zugangCompanion == true) "COMPANION_DASHBOARD" else "COMPANION_LOGIN"
            "/", "/login" -> if (LoginService.isLoggedIn() && LoginService.getUser()?.zugangDesktop == true) "NETZWERK_DASHBOARD" else "NETZWERK_LOGIN"
            else -> "NONE"
        }
    }

    LaunchedEffect(Unit) {
        LoginService.restoreSession()
        if (path == "/driver" || path == "/mobile" || path == "/companion") {
            if (LoginService.isLoggedIn()) LoginService.logout()
            sessionReady = true
            screen = when (path) {
                "/driver" -> "DRIVER_LOGIN"
                "/mobile" -> "DRIVER_LOGIN"
                "/companion" -> "COMPANION_LOGIN"
                else -> "NETZWERK_LOGIN"
            }
            return@LaunchedEffect
        }
        if (shouldForceLogoutAfterBuildChange("NET")) {
            LoginService.logout()
            sessionReady = true
            screen = when (path) {
                "/driver" -> "DRIVER_LOGIN"
                "/mobile" -> "DRIVER_LOGIN"
                "/companion" -> "COMPANION_LOGIN"
                "/", "/login" -> "NETZWERK_LOGIN"
                else -> "NONE"
            }
            return@LaunchedEffect
        }

        if (!LoginService.isLoggedIn()) {
            sessionReady = true
            screen = when (path) {
                "/driver" -> "DRIVER_LOGIN"
                "/mobile" -> "DRIVER_LOGIN"
                "/companion" -> "COMPANION_LOGIN"
                "/", "/login" -> "NETZWERK_LOGIN"
                else -> "NONE"
            }
            return@LaunchedEffect
        }

        if (path.isBlank()) {
            sessionReady = true
            screen = "NONE"
            return@LaunchedEffect
        }

        sessionReady = false
        val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
        if (initialized) {
            sessionReady = true
            screen = when (path) {
                "/driver" -> if (LoginService.getUser()?.zugangMobil == true) "DRIVER_DASHBOARD" else {
                    LoginService.logout()
                    "DRIVER_LOGIN"
                }
                "/mobile" -> if (LoginService.getUser()?.zugangMobil == true) "DRIVER_DASHBOARD" else {
                    LoginService.logout()
                    "DRIVER_LOGIN"
                }
                "/companion" -> if (LoginService.getUser()?.zugangCompanion == true) "COMPANION_DASHBOARD" else {
                    LoginService.logout()
                    "COMPANION_LOGIN"
                }
                "/", "/login" -> if (LoginService.getUser()?.zugangDesktop == true) "NETZWERK_DASHBOARD" else {
                    LoginService.logout()
                    "NETZWERK_LOGIN"
                }
                else -> "NONE"
            }
            return@LaunchedEffect
        }

        LoginService.logout()
        sessionReady = true
        screen = when (path) {
            "/driver" -> "DRIVER_LOGIN"
            "/mobile" -> "DRIVER_LOGIN"
            "/companion" -> "COMPANION_LOGIN"
            "/", "/login" -> "NETZWERK_LOGIN"
            else -> "NONE"
        }
    }

    LaunchedEffect(screenConfigVersion) {
        if (!LoginService.isLoggedIn()) return@LaunchedEffect
        runCatching { ScreenManager.applyScreenConfig(ScreenConfigApi.loadScreenConfig()) }
    }

    if (path == "/passwort-reset") {
        PasswordResetScreen { mode ->
            window.location.href = when (mode) {
                "driver" -> "/driver"
                "companion" -> "/companion"
                else -> "/login"
            }
            screen = when (mode) {
                "driver" -> "DRIVER_LOGIN"
                "companion" -> "COMPANION_LOGIN"
                else -> "NETZWERK_LOGIN"
            }
        }
        return
    }

    if (path == "/zwei-faktor-reset") {
        ZweiFaktorRecoveryScreen(ZweiFaktorRecoveryBereich.NET) {
            window.location.href = "/login"
            screen = "NETZWERK_LOGIN"
        }
        return
    }

    if (!sessionReady) {
        org.example.project.utils.DashboardVerlaufHintergrund()
        return
    }

    when (screen) {
        "NETZWERK_LOGIN" -> DesktopRegistrierungScreen(
            onSubmit = {
                scope.launch {
                    sessionReady = false
                    val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
                    if (!initialized) {
                        LoginService.logout()
                        sessionReady = true
                        screen = "NETZWERK_LOGIN"
                        return@launch
                    }
                    sessionReady = true
                    screen = "NETZWERK_DASHBOARD"
                }
            },
            externalScreen = "login"
        )
        "NETZWERK_DASHBOARD" -> {
            if (!LoginService.isLoggedIn()) {
                screen = "NETZWERK_LOGIN"
            } else if (LoginService.getUser()?.zugangDesktop != true) {
                LoginService.logout()
                screen = "NETZWERK_LOGIN"
            } else {
                DashboardScreen(
                    node = DashboardNode.NET,
                    onLogout = {
                        LoginService.logout()
                        screen = "NETZWERK_LOGIN"
                    }
                )
            }
        }
        "DRIVER_LOGIN" -> MobileFahrerRegistrierungScreen(
            onComplete = {
                screen = "DRIVER_DASHBOARD"
            },
            externalScreen = "LOGIN"
        )
        "DRIVER_DASHBOARD" -> {
            if (!LoginService.isLoggedIn()) {
                screen = "DRIVER_LOGIN"
            } else if (LoginService.getUser()?.zugangMobil != true) {
                LoginService.logout()
                screen = "DRIVER_LOGIN"
            } else {
                DriverDashboardScreen(
                    art = NodeScope.NET,
                    screens = driverMobileScreens(),
                    onOpenProfile = { },
                    onLogout = {
                        LoginService.logout()
                        screen = "DRIVER_LOGIN"
                    }
                )
            }
        }
        "COMPANION_LOGIN" -> CompanionLoginScreen(
            onComplete = { screen = "COMPANION_DASHBOARD" }
        )
        "COMPANION_DASHBOARD" -> {
            var bridgeError by remember { mutableStateOf(false) }
            LaunchedEffect(Unit) {
                val connected = runCatching {
                    org.example.project.service.CompanionBridgeService.connect()
                }.getOrDefault(false)
                if (connected) window.location.reload() else bridgeError = true
            }
            if (bridgeError) {
                org.jetbrains.compose.web.dom.Text("Companion-Verbindung zum zuständigen Node konnte nicht hergestellt werden.")
            }
        }
        else -> org.example.project.utils.DashboardVerlaufHintergrund()
    }
}
